/// <amd-module name="TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { GroupPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { IPermissionPage } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphMember, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { AadGraphMember, GraphGroup, GraphServicePrincipal, GraphUser } from "VSS/Platform/Contracts/Graph";
import { IIdentity } from "VSSUI/IdentityPicker";

export const GroupsViewActions = {
    initView: () => createAction(GroupsViewActionTypes.InitView),
    initialLoadSucceeded: (isCollectionNavigationAllowed: boolean, scopeDescriptor?: string) => createAction(GroupsViewActionTypes.InitialLoadSucceeded, { isCollectionNavigationAllowed, scopeDescriptor }),
    logTelemetry: (component: GroupsComponent, action: string, properties?: { [key: string]: any }, errorMessage?: string, feature?: string) =>
        createAction(GroupsViewActionTypes.LogTelemetry, { component, action, properties, errorMessage, feature }),

    // Groups page actions
    selectedGroupChanged: (group: PermissionsGroup) => createAction(GroupsViewActionTypes.SelectedGroupChanged, group),
    getSelectedGroupPermissionsSucceeded: (descriptor: string, permissions: GroupPermissions) =>
        createAction(GroupsViewActionTypes.SelectedGroupPermissionsUpdate, { descriptor, permissions }),

    // Users page actions
    selectedUserChanged: (user: GraphUser) => createAction(GroupsViewActionTypes.SelectedUserChanged, user),
    selectedUserChangedSuccessful: (user: GraphUser) => createAction(GroupsViewActionTypes.SelectedUserChangedSuccessful, user),

    // Service principals page actions
    selectedServicePrincipalChanged: (servicePrincipal: GraphServicePrincipal) => createAction(GroupsViewActionTypes.SelectedServicePrincipalChanged, servicePrincipal),
    selectedServicePrincipalChangedSuccessful: (servicePrincipal: GraphServicePrincipal) => createAction(GroupsViewActionTypes.SelectedServicePrincipalChangedSuccessful, servicePrincipal),

    // Members page actions
    selectedMemberChanged: (member: AadGraphMember) => createAction(GroupsViewActionTypes.SelectedMemberChanged, member),
    selectedMemberChangedSuccessful: (member: CustomGraphMember) => createAction(GroupsViewActionTypes.SelectedMemberChangedSuccessful, member),

    // Change project default team permission
    canEditProjectLevelInformation: (permission: boolean) => createAction(GroupsViewActionTypes.CanEditProjectLevelInformation, permission),

    permissionPage: (permissionPage: IPermissionPage) => createAction(GroupsViewActionTypes.PermissionPage, permissionPage),

    clearSelectedIdentity: () => createAction(GroupsViewActionTypes.ClearSelectedIdentity),
    groupEdited: (group: GraphGroup) => createAction(GroupsViewActionTypes.GroupEdited, group),
    groupAvatarUpdatedUrl: (url: string) => createAction(GroupsViewActionTypes.GroupAvatarUpdatedUrl, url),
    setFilterPlaceholder: (placeholder: string) => createAction(GroupsViewActionTypes.SetFilterPlaceHolder, placeholder),
    setIsTeamsPageFlag: (isTeamsPage: boolean) => createAction(GroupsViewActionTypes.SetIsTeamsPageFlag, isTeamsPage),
    setPermissionsMarkTTI: () => createAction(GroupsViewActionTypes.SetPermissionsMarkTTI),
    setURLRedirectFlag: () => createAction(GroupsViewActionTypes.SetURLRedirectFlag),

    // Add member actions
    closeAddMemberPanel: () => createAction(GroupsViewActionTypes.CloseAddMemberPanel),
    openAddMemberPanel: () => createAction(GroupsViewActionTypes.OpenAddMemberPanel),
    onSaveMembers: (subjectIdentities: IIdentity[], selectedGroup: PermissionsGroup, scopeDescriptor: string | undefined) =>
        createAction(GroupsViewActionTypes.SaveMembers, { subjectIdentities, selectedGroup, scopeDescriptor }),

    closePCAMessageBar: () => createAction(GroupsViewActionTypes.ClosePCAMessageBar),
    setPcaGroup: (pcaGroup: PermissionsGroup, wellKnownGroups: PermissionsGroup[]) =>
        createAction(GroupsViewActionTypes.SetPcaGroup, { pcaGroup, wellKnownGroups }),
    setPcaGroupSuccess: (pcaGroup: PermissionsGroup) => createAction(GroupsViewActionTypes.SetPcaGroupSuccess, { pcaGroup }),
    checkMembershipExistence: (member: IIdentity, groupDescriptor: string) =>
        createAction(GroupsViewActionTypes.CheckMembershipExistence, { member, groupDescriptor }),

    // Add & remove identities actions
    addInvalidMember: (member: IIdentity) => createAction(GroupsViewActionTypes.AddInvalidMember, { member }),
    addValidMember: (member: IIdentity) => createAction(GroupsViewActionTypes.AddValidMember, { member }),
    removeMembers: (members: IIdentity[]) => createAction(GroupsViewActionTypes.RemoveMembers, { members })
};

export const enum GroupsViewActionTypes {
    InitView = "AdminGroups/Init",
    InitialLoadSucceeded = "AdminGroups/InitialLoadSucceeded",

    LogTelemetry = "AdminGroups/LogTelemetry",

    // Groups page actions
    SelectedGroupChanged = "AdminGroups/SelectedGroupChanged",
    SelectedGroupPermissionsUpdate = "AdminGroups/SelectedGroupPermissionsUpdate",
    CanEditProjectLevelInformation = "AdminGroups/CanEditProjectLevelInformation",
    PermissionPage = "AdminGroups/PermissionPage",
    ClearSelectedIdentity = "AdminGroups/ClearSelectedIdentity",
    GroupEdited = "OrganizationGroups/GroupEdited",
    GroupAvatarUpdatedUrl = "OrganizationGroups/GroupAvatarUpdatedUrl",
    SetPermissionsMarkTTI = "OrganizationGroups/SetPermissionsMarkTTI",
    SetURLRedirectFlag = "OrganizationGroups/SetURLRedirectFlag",

    SelectedUserChanged = "AdminGroups/SelectedUserChanged",
    SelectedUserChangedSuccessful = "AdminGroups/SelectedMemberChangedSuccessful",
    SelectedMemberChanged = "AdminGroups/SelectedMemberChanged",
    SelectedMemberChangedSuccessful = "AdminGroups/SelectedMemberChangedSuccessful",
    SelectedServicePrincipalChanged = "AdminGroups/SelectedServicePrincipalChanged",
    SelectedServicePrincipalChangedSuccessful = "AdminGroups/SelectedServicePrincipalChangedSuccessful",
    SetFilterPlaceHolder = "AdminGroups/SetFilterPlaceholder",
    SetIsTeamsPageFlag = "AdminGroups/SetIsTeamsPageFlag",

    ClosePCAMessageBar = "GroupsView/ClosePCAMessageBar",
    SetPcaGroup = "OrganizationGroups/SetPcaGroup",
    SetPcaGroupSuccess = "OrganizationGroups/SetPcaGroupSuccess",

    // Add member
    SaveMembers = "GroupsView/SaveMembers",
    CloseAddMemberPanel = "GroupsView/CloseAddMemberPanel",
    OpenAddMemberPanel = "GroupsView/OpenAddMemberPanel",
    CheckMembershipExistence = "OrganizationGroups/CheckMembershipExistence",
    AddInvalidMember = "OrganizationGroups/AddInvalidMember",
    AddValidMember = "OrganizationGroups/AddValidMember",
    RemoveMembers = "OrganizationGroups/RemoveMembers"
}

export type GroupsViewActions = ActionsUnion<typeof GroupsViewActions>;
